import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CACHE_KEYS, cacheUtils } from '../store/queryClient';
import { 
  getAllFaqData, 
  getCategoriesWithFolders,
  getFolderArticles,
  getArticleByIdWithLocale,
  getArticlesInFolder, 
  getArticleById,
  enhancedSearch,
  getLocales
} from '../services/freshdeskService';

// Development ortamında cache devre dışı - Her zaman fresh data
const isDevelopment = process.env.NODE_ENV === 'development';

// Kategoriler ve klasörleri getiren hook (yeni optimize edilmiş endpoint)
export const useCategoriesWithFolders = (language = 'en') => {
  return useQuery({
    queryKey: CACHE_KEYS.CATEGORIES_WITH_FOLDERS(language),
    queryFn: () => getCategoriesWithFolders(language),
    staleTime: isDevelopment ? 0 : 4 * 60 * 60 * 1000, // Development'ta 0, production'da 4 saat
    gcTime: isDevelopment ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000, // Development'ta 5 dk, production'da 24 saat
    retry: isDevelopment ? 3 : 0, // Development'ta 3 retry, production'da 0
    retryDelay: isDevelopment ? 2000 : 100, // Development'ta 2 saniye, production'da 100ms
    refetchOnWindowFocus: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnMount: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnReconnect: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    networkMode: isDevelopment ? 'online' : 'offlineFirst', // Development'ta online, production'da offline-first
    staleWhileRevalidate: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
    keepPreviousData: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
  });
};

// Klasör makalelerini getiren hook (yeni optimize edilmiş endpoint)
export const useFolderArticles = (folderId, language = 'en') => {
  return useQuery({
    queryKey: CACHE_KEYS.FOLDER_ARTICLES(folderId, language),
    queryFn: () => getFolderArticles(folderId, language),
    enabled: !!folderId,
    staleTime: isDevelopment ? 0 : 3 * 60 * 60 * 1000, // Development'ta 0, production'da 3 saat
    gcTime: isDevelopment ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000, // Development'ta 5 dk, production'da 24 saat
    retry: isDevelopment ? 3 : 0, // Development'ta 3 retry, production'da 0
    retryDelay: isDevelopment ? 2000 : 100, // Development'ta 2 saniye, production'da 100ms
    refetchOnWindowFocus: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnMount: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnReconnect: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    networkMode: isDevelopment ? 'online' : 'offlineFirst', // Development'ta online, production'da offline-first
    staleWhileRevalidate: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
    keepPreviousData: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
  });
};

// Tüm FAQ verilerini getiren hook - Development'ta cache devre dışı
export const useAllFaqData = (language = 'en') => {
  return useQuery({
    queryKey: CACHE_KEYS.ALL_FAQ_DATA(language),
    queryFn: () => getAllFaqData(language),
    staleTime: isDevelopment ? 0 : 4 * 60 * 60 * 1000, // Development'ta 0, production'da 4 saat
    gcTime: isDevelopment ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000, // Development'ta 5 dk, production'da 24 saat
    retry: isDevelopment ? 3 : 0, // Development'ta 3 retry, production'da 0
    retryDelay: isDevelopment ? 2000 : 100, // Development'ta 2 saniye, production'da 100ms
    refetchOnWindowFocus: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnMount: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnReconnect: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    networkMode: isDevelopment ? 'online' : 'offlineFirst', // Development'ta online, production'da offline-first
    staleWhileRevalidate: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
    keepPreviousData: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
  });
};

// Kategori makalelerini getiren hook - Development'ta cache devre dışı
export const useCategoryArticles = (categoryId, language = 'en') => {
  return useQuery({
    queryKey: CACHE_KEYS.FAQ_ARTICLES(categoryId),
    queryFn: () => getArticlesInFolder(categoryId, language),
    enabled: !!categoryId,
    staleTime: isDevelopment ? 0 : 3 * 60 * 60 * 1000, // Development'ta 0, production'da 3 saat
    gcTime: isDevelopment ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000, // Development'ta 5 dk, production'da 24 saat
    retry: isDevelopment ? 3 : 0, // Development'ta 3 retry, production'da 0
    retryDelay: isDevelopment ? 2000 : 100, // Development'ta 2 saniye, production'da 100ms
    refetchOnWindowFocus: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnMount: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnReconnect: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    networkMode: isDevelopment ? 'online' : 'offlineFirst', // Development'ta online, production'da offline-first
    staleWhileRevalidate: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
    keepPreviousData: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
  });
};

// Eski useFolderArticles fonksiyonu kaldırıldı - yeni useFolderArticles kullanılıyor

// Tekil makale getiren hook - Development'ta cache devre dışı
export const useArticle = (articleId, language = 'en') => {
  return useQuery({
    queryKey: CACHE_KEYS.FAQ_ARTICLE(articleId),
    queryFn: () => getArticleById(articleId, language),
    enabled: !!articleId,
    staleTime: isDevelopment ? 0 : 6 * 60 * 60 * 1000, // Development'ta 0, production'da 6 saat
    gcTime: isDevelopment ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000, // Development'ta 5 dk, production'da 24 saat
    retry: isDevelopment ? 3 : 0, // Development'ta 3 retry, production'da 0
    retryDelay: isDevelopment ? 2000 : 100, // Development'ta 2 saniye, production'da 100ms
    refetchOnWindowFocus: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnMount: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnReconnect: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    networkMode: isDevelopment ? 'online' : 'offlineFirst', // Development'ta online, production'da offline-first
    staleWhileRevalidate: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
    keepPreviousData: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
  });
};

// Tekil makale getiren hook - Yeni optimize edilmiş endpoint ile
export const useArticleWithLocale = (articleId, language = 'en') => {
  return useQuery({
    queryKey: CACHE_KEYS.ARTICLE_WITH_LOCALE(articleId, language),
    queryFn: () => getArticleByIdWithLocale(articleId, language),
    enabled: !!articleId,
    staleTime: isDevelopment ? 0 : 6 * 60 * 60 * 1000, // Development'ta 0, production'da 6 saat
    gcTime: isDevelopment ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000, // Development'ta 5 dk, production'da 24 saat
    retry: isDevelopment ? 3 : 0, // Development'ta 3 retry, production'da 0
    retryDelay: isDevelopment ? 2000 : 100, // Development'ta 2 saniye, production'da 100ms
    refetchOnWindowFocus: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnMount: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnReconnect: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    networkMode: isDevelopment ? 'online' : 'offlineFirst', // Development'ta online, production'da offline-first
    staleWhileRevalidate: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
    keepPreviousData: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
  });
};

// Arama sonuçlarını getiren hook - Development'ta cache devre dışı
export const useSearchResults = (query, sortBy = 'relevance', categoryId = null) => {
  return useQuery({
    queryKey: CACHE_KEYS.SEARCH_RESULTS(query),
    queryFn: () => enhancedSearch(query, sortBy, categoryId),
    enabled: !!query && query.length > 2, // En az 3 karakter
    staleTime: isDevelopment ? 0 : 30 * 60 * 1000, // Development'ta 0, production'da 30 dakika
    gcTime: isDevelopment ? 5 * 60 * 1000 : 2 * 60 * 60 * 1000, // Development'ta 5 dk, production'da 2 saat
    retry: isDevelopment ? 3 : 0, // Development'ta 3 retry, production'da 0
    retryDelay: isDevelopment ? 2000 : 100, // Development'ta 2 saniye, production'da 100ms
    refetchOnWindowFocus: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnMount: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnReconnect: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    networkMode: isDevelopment ? 'online' : 'offlineFirst', // Development'ta online, production'da offline-first
    staleWhileRevalidate: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
    keepPreviousData: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
  });
};

// Popüler makaleler hook'u - Development'ta cache devre dışı
export const usePopularArticles = (language = 'en') => {
  return useQuery({
    queryKey: CACHE_KEYS.POPULAR_ARTICLES(language),
    queryFn: () => getAllFaqData(language), // Şimdilik aynı API'yi kullan
    staleTime: isDevelopment ? 0 : 6 * 60 * 60 * 1000, // Development'ta 0, production'da 6 saat
    gcTime: isDevelopment ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000, // Development'ta 5 dk, production'da 24 saat
    retry: isDevelopment ? 3 : 0, // Development'ta 3 retry, production'da 0
    retryDelay: isDevelopment ? 2000 : 100, // Development'ta 2 saniye, production'da 100ms
    refetchOnWindowFocus: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnMount: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnReconnect: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    networkMode: isDevelopment ? 'online' : 'offlineFirst', // Development'ta online, production'da offline-first
    staleWhileRevalidate: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
    keepPreviousData: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
  });
};

// Son görüntülenen makaleler hook'u - localStorage tabanlı
export const useRecentArticles = () => {
  return useQuery({
    queryKey: CACHE_KEYS.RECENT_ARTICLES,
    queryFn: () => {
      try {
        const recent = localStorage.getItem('recent-articles');
        return recent ? JSON.parse(recent) : [];
      } catch {
        return [];
      }
    },
    staleTime: Infinity, // Hiçbir zaman stale olmasın
    gcTime: Infinity, // Hiçbir zaman silinmesin
    retry: 0, // Hiç retry yapma
    refetchOnWindowFocus: false, // Window focus'ta refetch yapma
    refetchOnMount: false, // Mount'ta refetch yapma
    refetchOnReconnect: false, // Reconnect'te refetch yapma
    networkMode: 'offlineFirst', // Offline-first
  });
};

// Desteklenen dilleri getiren hook - Development'ta cache devre dışı
export const useLocales = () => {
  return useQuery({
    queryKey: CACHE_KEYS.LOCALES,
    queryFn: () => getLocales(),
    staleTime: isDevelopment ? 0 : 24 * 60 * 60 * 1000, // Development'ta 0, production'da 24 saat
    gcTime: isDevelopment ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000, // Development'ta 5 dk, production'da 24 saat
    retry: isDevelopment ? 3 : 0, // Development'ta 3 retry, production'da 0
    retryDelay: isDevelopment ? 2000 : 100, // Development'ta 2 saniye, production'da 100ms
    refetchOnWindowFocus: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnMount: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    refetchOnReconnect: isDevelopment ? true : false, // Development'ta açık, production'da kapalı
    networkMode: isDevelopment ? 'online' : 'offlineFirst', // Development'ta online, production'da offline-first
    staleWhileRevalidate: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
    keepPreviousData: isDevelopment ? false : true, // Development'ta kapalı, production'da açık
  });
};

// Cache temizleme hook'ları
export const useCacheInvalidation = () => {
  const queryClient = useQueryClient();

  const invalidateCategory = (categoryId) => {
    queryClient.invalidateQueries({
      queryKey: CACHE_KEYS.FAQ_ARTICLES(categoryId),
    });
  };

  const invalidateFolder = (categoryId, folderId) => {
    queryClient.invalidateQueries({
      queryKey: CACHE_KEYS.FAQ_FOLDER_ARTICLES(categoryId, folderId),
    });
  };

  const invalidateArticle = (articleId) => {
    queryClient.invalidateQueries({
      queryKey: CACHE_KEYS.FAQ_ARTICLE(articleId),
    });
  };

  const invalidateSearch = (query) => {
    queryClient.invalidateQueries({
      queryKey: CACHE_KEYS.SEARCH_RESULTS(query),
    });
  };

  const invalidateAllFaq = () => {
    queryClient.invalidateQueries({
      queryKey: ['faq'],
    });
  };

  const clearAllCache = () => {
    queryClient.clear();
  };

  return {
    invalidateCategory,
    invalidateFolder,
    invalidateArticle,
    invalidateSearch,
    invalidateAllFaq,
    clearAllCache,
  };
};

// Prefetch hook'ları (agresif önceden yükleme)
export const usePrefetch = () => {
  const queryClient = useQueryClient();

  const prefetchArticle = (articleId, language = 'en') => {
    queryClient.prefetchQuery({
      queryKey: CACHE_KEYS.FAQ_ARTICLE(articleId),
      queryFn: () => getArticleById(articleId, language),
      staleTime: 6 * 60 * 60 * 1000, // 6 saat
      gcTime: 24 * 60 * 60 * 1000, // 24 saat
    });
  };

  const prefetchCategory = (categoryId, language = 'en') => {
    queryClient.prefetchQuery({
      queryKey: CACHE_KEYS.FAQ_ARTICLES(categoryId),
      queryFn: () => getArticlesInFolder(categoryId, language),
      staleTime: 3 * 60 * 60 * 1000, // 3 saat
      gcTime: 24 * 60 * 60 * 1000, // 24 saat
    });
  };

  const prefetchFolder = (categoryId, folderId, language = 'en') => {
    queryClient.prefetchQuery({
      queryKey: CACHE_KEYS.FAQ_FOLDER_ARTICLES(categoryId, folderId),
      queryFn: () => getArticlesInFolder(categoryId, language, folderId),
      staleTime: 3 * 60 * 60 * 1000, // 3 saat
      gcTime: 24 * 60 * 60 * 1000, // 24 saat
    });
  };

  const aggressivePrefetch = (queries) => {
    cacheUtils.aggressivePrefetch(queries);
  };

  return {
    prefetchArticle,
    prefetchCategory,
    prefetchFolder,
    aggressivePrefetch,
  };
};

// Cache yönetimi hook'u
export const useCacheManagement = () => {
  const queryClient = useQueryClient();

  const getCacheStats = () => {
    return cacheUtils.getCacheStats();
  };

  const persistCache = () => {
    cacheUtils.persistCache();
  };

  const loadPersistedCache = () => {
    cacheUtils.loadPersistedCache();
  };

  const addToRecentArticles = (article) => {
    try {
      const recent = JSON.parse(localStorage.getItem('recent-articles') || '[]');
      const filtered = recent.filter(item => item.id !== article.id);
      const updated = [article, ...filtered].slice(0, 10); // Son 10 makale
      localStorage.setItem('recent-articles', JSON.stringify(updated));
      
      // Cache'i güncelle
      queryClient.setQueryData(CACHE_KEYS.RECENT_ARTICLES, updated);
    } catch (error) {
      console.warn('Failed to add to recent articles:', error);
    }
  };

  return {
    getCacheStats,
    persistCache,
    loadPersistedCache,
    addToRecentArticles,
  };
}; 